/**
 * Servicio para exportación de datos en diferentes formatos
 * Soporta CSV, Excel y PDF
 */

import { exportPayments } from './billing';
import * as XLSX from 'xlsx';
import { formatChileFullDateTime, getChileNow } from '../utils/dateUtils';

/**
 * Convertir datos a formato CSV
 * @param {Array} data - Datos a convertir
 * @param {Array} headers - Encabezados de las columnas
 * @returns {string} Contenido CSV
 */
const convertToCSV = (data, headers) => {
  const csvHeaders = headers.join(',');
  const csvRows = data.map(row => 
    headers.map(header => {
      const value = row[header] || '';
      // Escapar comillas y comas
      const escapedValue = typeof value === 'string' 
        ? `"${value.replace(/"/g, '""')}"` 
        : value;
      return escapedValue;
    }).join(',')
  );
  
  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Descargar archivo como blob
 * @param {string} content - Contenido del archivo
 * @param {string} filename - Nombre del archivo
 * @param {string} mimeType - Tipo MIME del archivo
 */
const downloadFile = (content, filename, mimeType) => {
  // Solo agregar BOM para archivos CSV (texto plano)
  let finalContent = content;
  let finalMimeType = mimeType;
  
  if (filename.toLowerCase().endsWith('.csv')) {
    // BOM para UTF-8 - necesario para que Excel muestre correctamente caracteres especiales
    const BOM = '\uFEFF';
    finalContent = BOM + content;
    finalMimeType = 'text/csv;charset=utf-8';
  } else if (filename.toLowerCase().endsWith('.xls')) {
    // Para archivos Excel (formato TSV), agregar BOM para caracteres especiales
    const BOM = '\uFEFF';
    finalContent = BOM + content;
    finalMimeType = 'application/vnd.ms-excel;charset=utf-8';
  } else {
    // Para otros archivos (xlsx binarios), mantener tipo MIME original
    finalMimeType = mimeType;
  }
  
  const blob = new Blob([finalContent], { type: finalMimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generar nombre de archivo con timestamp
 * @param {string} baseName - Nombre base del archivo
 * @param {string} extension - Extensión del archivo
 * @param {Object} filters - Filtros aplicados (opcional)
 * @returns {string} Nombre completo del archivo
 */
const generateFilename = (baseName, extension, filters = {}) => {
  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  
  // Si es una exportación de selección, indicarlo en el nombre
  if (filters.selected_order_ids && filters.selected_order_ids.length > 0) {
    return `${baseName}_seleccion_${filters.selected_order_ids.length}_${timestamp}.${extension}`;
  }
  
  const period = filters.start_date && filters.end_date 
    ? `_${filters.start_date}_${filters.end_date}`
    : `_${timestamp}`;
  
  return `${baseName}${period}.${extension}`;
};

/**
 * Exportar pagos a CSV
 * @param {Object} filters - Filtros aplicados
 * @returns {Promise} Proceso de exportación
 */
export const exportPaymentsToCSV = async (filters = {}) => {
  try {
    // Obtener datos para exportación
    const exportData = await exportPayments(filters, 'csv');
    
    // Definir encabezados
    const headers = [
      'id',
      'fecha_pago',
      'nombre',
      'email',
      'monto',
      'estado',
      'metodo_pago',
      'concepto',
      'mes',
      'grupo',
      'numero_boleta'
    ];
    
    // Convertir a CSV
    const csvContent = convertToCSV(exportData.data, headers);
    
    // Generar nombre de archivo
    const filename = generateFilename('pagos', 'csv', filters);
    
    // Descargar archivo
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
    
    return {
      success: true,
      filename,
      recordCount: exportData.total,
      format: 'CSV'
    };
    
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error('Error al exportar a CSV: ' + error.message);
  }
};

/**
 * Crear workbook Excel con estructura y metadatos
 * @param {Object} exportData - Datos de exportación
 * @param {Object} filters - Filtros aplicados
 * @returns {Object} Workbook de Excel
 */
const createExcelWorkbook = (exportData, filters) => {
  // Crear nuevo workbook
  const wb = XLSX.utils.book_new();
  
  // Preparar datos para la hoja principal
  const worksheetData = [];
  
  // Encabezados
  const headers = [
    'ID',
    'Fecha de Pago',
    'Alumno',
    'Email',
    'Monto CLP',
    'Estado',
    'Metodo de Pago',
    'Concepto',
    'Mes',
    'Grupo Curso',
    'Número de Boleta'
  ];
  
  worksheetData.push(headers);
  
  // Datos del reporte
  exportData.data.forEach(row => {
    const rowData = [
      parseInt(row.id) || 0,
      row.fecha_pago || '',
      (row.nombre || '').toString().replace(/[\t\n\r]/g, ' '),
      row.email || '',
      parseInt(row.monto) || 0,
      (row.estado || '').toString().replace(/[\t\n\r]/g, ' '),
      (row.metodo_pago || '').toString().replace(/[\t\n\r]/g, ' '),
      (row.concepto || '').toString().replace(/[\t\n\r]/g, ' '),
      row.mes || '',
      (row.grupo || '').toString().replace(/[\t\n\r]/g, ' '),
      (row.numero_boleta || '').toString().replace(/[\t\n\r]/g, ' ')
    ];
    worksheetData.push(rowData);
  });
  
  // Crear worksheet principal
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Aplicar formato a la hoja
  addFormattingToWorksheet(ws, worksheetData.length);
  
  // Agregar hoja principal al workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Pagos');
  
  // Crear hoja de metadatos
  const metadataData = [
    ['REPORTE DE PAGOS'],
    [''],
    ['Información del Reporte'],
    ['Generado:', getChileNow()],
    ['Período:', `${filters.start_date || 'N/A'} - ${filters.end_date || 'N/A'}`],
    ['Total registros:', exportData.total],
    ['Estado filtrado:', filters.status === 'all' ? 'Todos' : filters.status || 'Todos'],
    [''],
    ['Descripción de Columnas'],
    ['ID', 'Identificador único del pago'],
    ['Fecha', 'Fecha de creación del pago'],
    ['Fecha Modificación', 'Última fecha de modificación'],
    ['Alumno', 'Nombre completo del estudiante'],
    ['Email', 'Correo electrónico de contacto'],
    ['Monto CLP', 'Monto en pesos chilenos'],
    ['Estado', 'Estado actual del pago (completed, pending, etc.)'],
    ['Método de Pago', 'Forma de pago utilizada'],
    ['Concepto', 'Descripción del concepto de pago'],
    ['Mes', 'Período al que corresponde el pago'],
    ['Grupo Curso', 'Curso al que pertenece el estudiante']
  ];
  
  const wsMetadata = XLSX.utils.aoa_to_sheet(metadataData);
  
  // Formato para la hoja de metadatos
  wsMetadata['!cols'] = [
    { wch: 20 }, // Columna de etiquetas
    { wch: 40 }  // Columna de valores
  ];
  
  // Agregar hoja de metadatos al workbook
  XLSX.utils.book_append_sheet(wb, wsMetadata, 'Información');
  
  return wb;
};

/**
 * Aplicar formato y anchos de columna a la hoja de trabajo
 * @param {Object} ws - Worksheet de Excel
 * @param {number} rowCount - Número de filas de datos
 */
const addFormattingToWorksheet = (ws, rowCount) => {
  // Configurar anchos de columna optimizados
  ws['!cols'] = [
    { wch: 8 },   // ID
    { wch: 16 },  // Fecha de Pago
    { wch: 35 },  // Alumno
    { wch: 30 },  // Email
    { wch: 12 },  // Monto CLP
    { wch: 12 },  // Estado
    { wch: 15 },  // Método de Pago
    { wch: 20 },  // Concepto
    { wch: 10 },  // Mes
    { wch: 25 }   // Grupo Curso
  ];
  
  // Aplicar formato a las celdas de encabezado (fila 1)
  const headerRange = XLSX.utils.decode_range(ws['!ref']);
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellAddress]) continue;
    
    // Aplicar formato de encabezado
    ws[cellAddress].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "366092" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" }
      }
    };
  }
  
  // Aplicar formato a las celdas de datos
  for (let row = 1; row < rowCount; row++) {
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      if (!ws[cellAddress]) continue;
      
      let alignment = { vertical: "center" };
      
      // Alineación específica por columna
      if (col === 0 || col === 4) { // ID y Monto - derecha
        alignment.horizontal = "right";
      } else if (col === 1 || col === 8) { // Fecha de Pago y Mes - centro
        alignment.horizontal = "center";
      } else { // Resto - izquierda
        alignment.horizontal = "left";
      }
      
      // Aplicar formato de celda de datos
      ws[cellAddress].s = {
        alignment: alignment,
        border: {
          top: { style: "thin", color: { rgb: "E0E0E0" } },
          bottom: { style: "thin", color: { rgb: "E0E0E0" } },
          left: { style: "thin", color: { rgb: "E0E0E0" } },
          right: { style: "thin", color: { rgb: "E0E0E0" } }
        }
      };
      
      // Formato de número para montos
      if (col === 5 && ws[cellAddress].v) {
        ws[cellAddress].z = '"$"#,##0';
      }
    }
  }
  
  // Configurar auto-filtro para los encabezados
  ws['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headerRange.e.c)}1` };
};

/**
 * Generar archivo Excel binario y descargarlo
 * @param {Object} workbook - Workbook de Excel
 * @param {string} filename - Nombre del archivo
 */
const downloadExcelFile = (workbook, filename) => {
  // Generar archivo Excel binario
  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array',
    compression: true 
  });
  
  // Crear blob y descargar
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Exportar pagos a Excel (usando librería XLSX para Excel binario real)
 * @param {Object} filters - Filtros aplicados
 * @returns {Promise} Proceso de exportación
 */
export const exportPaymentsToExcel = async (filters = {}) => {
  try {
    // Obtener datos para exportación
    const exportData = await exportPayments(filters, 'excel');
    
    // Crear workbook estructurado con metadatos y formato
    const workbook = createExcelWorkbook(exportData, filters);
    
    // Generar nombre de archivo (cambiar extensión a xlsx)
    const filename = generateFilename('pagos', 'xlsx', filters);
    
    // Descargar archivo Excel binario
    downloadExcelFile(workbook, filename);
    
    return {
      success: true,
      filename,
      recordCount: exportData.total,
      format: 'Excel'
    };
    
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Error al exportar a Excel: ' + error.message);
  }
};

/**
 * Exportar pagos a PDF (formato de reporte)
 * @param {Object} filters - Filtros aplicados
 * @returns {Promise} Proceso de exportación
 */
export const exportPaymentsToPDF = async (filters = {}) => {
  try {
    // Obtener datos para exportación
    const exportData = await exportPayments(filters, 'pdf');
    
    // Crear contenido HTML para conversión a PDF
    const htmlContent = generatePDFHTMLContent(exportData, filters);
    
    // Usar window.print() para generar PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Trigger print dialog
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
    
    return {
      success: true,
      filename: generateFilename('pagos', 'pdf', filters),
      recordCount: exportData.total,
      format: 'PDF'
    };
    
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Error al exportar a PDF: ' + error.message);
  }
};

/**
 * Generar contenido HTML para PDF
 * @param {Object} exportData - Datos de exportación
 * @param {Object} filters - Filtros aplicados
 * @returns {string} Contenido HTML
 */
const generatePDFHTMLContent = (exportData, filters) => {
  const currentDate = getChileNow();
  const period = filters.start_date && filters.end_date 
    ? `${filters.start_date} - ${filters.end_date}`
    : 'Todo el período';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Reporte de Pagos</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          font-size: 12px;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        .header h1 {
          color: #333;
          margin: 0;
          font-size: 24px;
        }
        .metadata {
          display: flex;
          justify-content: space-between;
          margin: 20px 0;
          background-color: #f5f5f5;
          padding: 15px;
          border-radius: 5px;
        }
        .metadata div {
          flex: 1;
        }
        .metadata strong {
          color: #333;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
          color: #333;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .status-completed { color: #28a745; font-weight: bold; }
        .status-pending { color: #ffc107; font-weight: bold; }
        .status-failed { color: #dc3545; font-weight: bold; }
        .amount { text-align: right; font-weight: bold; }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 10px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Reporte de Pagos</h1>
        <p>Sistema CRM Educativo - Datofy Suscripciones</p>
      </div>
      
      <div class="metadata">
        <div>
          <strong>Período:</strong><br>
          ${period}
        </div>
        <div>
          <strong>Total registros:</strong><br>
          ${exportData.total.toLocaleString('es-CL')}
        </div>
        <div>
          <strong>Estado filtrado:</strong><br>
          ${filters.status === 'all' ? 'Todos' : filters.status || 'Todos'}
        </div>
        <div>
          <strong>Generado:</strong><br>
          ${currentDate}
        </div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Fecha</th>
            <th>Alumno</th>
            <th>Email</th>
            <th>Concepto</th>
            <th>Monto</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          ${exportData.data.map(row => `
            <tr>
              <td>${row.id}</td>
              <td>${row.fecha}</td>
              <td>${row.nombre}</td>
              <td>${row.email}</td>
              <td>${row.concepto}</td>
              <td class="amount">$${parseInt(row.monto).toLocaleString('es-CL')}</td>
              <td class="status-${row.estado}">${row.estado}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div class="footer">
        <p>Este reporte fue generado automáticamente por el Sistema CRM Educativo</p>
        <p>Fecha de generación: ${currentDate}</p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Función principal de exportación que maneja todos los formatos
 * @param {string} format - Formato de exportación ('csv', 'excel', 'pdf')
 * @param {Object} filters - Filtros aplicados
 * @param {Array} selectedOrderIds - IDs de órdenes seleccionadas (opcional)
 * @returns {Promise} Resultado de la exportación
 */
export const exportPaymentData = async (format, filters = {}, selectedOrderIds = null) => {
  try {
    // Si hay IDs seleccionadas, usarlas; sino usar filtros normales
    const exportFilters = selectedOrderIds && selectedOrderIds.length > 0 
      ? { ...filters, selected_order_ids: selectedOrderIds }
      : filters;
    
    switch (format.toLowerCase()) {
      case 'csv':
        return await exportPaymentsToCSV(exportFilters);
      case 'excel':
      case 'xlsx':
      case 'xls':
        return await exportPaymentsToExcel(exportFilters);
      case 'pdf':
        return await exportPaymentsToPDF(exportFilters);
      default:
        throw new Error(`Formato de exportación no soportado: ${format}`);
    }
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

/**
 * Obtener información sobre formatos disponibles
 * @returns {Array} Lista de formatos disponibles
 */
export const getAvailableFormats = () => {
  return [
    {
      value: 'csv',
      label: 'CSV (Comma Separated Values)',
      icon: 'FaFileCsv',
      description: 'Compatible con Excel y hojas de cálculo',
      mimeType: 'text/csv'
    },
    {
      value: 'excel',
      label: 'Excel (.xlsx)',
      icon: 'FaFileExcel',
      description: 'Formato moderno de Microsoft Excel con columnas alineadas',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    {
      value: 'pdf',
      label: 'PDF (Portable Document Format)',
      icon: 'FaFilePdf',
      description: 'Reporte imprimible en formato PDF',
      mimeType: 'application/pdf'
    }
  ];
};

export default {
  exportPaymentData,
  exportPaymentsToCSV,
  exportPaymentsToExcel,
  exportPaymentsToPDF,
  getAvailableFormats
};